/** DOM の準備と表示の切り替え begin */
/**
 * DOM の準備ができたら fn を実行する（jQuery 3 の $(fn) の置き換え）。
 * $(fn) と同じく、DOMContentLoaded のあとに別のタスクとして実行する。ほかの DOMContentLoaded
 * （このファイルの「配達日」や build.js）で用意される値を、待ってから使えるようにするため。
 * DOM の準備がもう終わっていれば、次のタスクで実行する。
 */
function onDomReady(fn) {
  const run = () => setTimeout(fn);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
}

const defaultDisplayByTagName = {};
function defaultDisplayOf(el) {
  const tagName = el.nodeName;
  if (!defaultDisplayByTagName[tagName]) {
    const probe = document.body.appendChild(document.createElement(tagName));
    const display = getComputedStyle(probe).display;
    probe.remove();
    defaultDisplayByTagName[tagName] = display === 'none' ? 'block' : display;
  }
  return defaultDisplayByTagName[tagName];
}

/** 隠す前のインラインの display。jQuery の .hide() / .show() と同じく、出すときに戻す */
const inlineDisplayBeforeHide = new WeakMap();

/** jQuery の .hide() と同じく、隠す前のインラインの display（例: style='display: flex'）を覚えてから隠す */
function hideElement(el) {
  if (el.style.display !== 'none') inlineDisplayBeforeHide.set(el, el.style.display);
  el.style.display = 'none';
}

/**
 * jQuery の .show() と同じく、インラインの display: none を、隠す前の値（無ければ空）に戻す。
 * それでも隠れていれば既定の display にする
 */
function showElement(el) {
  if (el.style.display === 'none') el.style.display = inlineDisplayBeforeHide.get(el) ?? '';
  if (el.style.display === '' && getComputedStyle(el).display === 'none') {
    el.style.display = defaultDisplayOf(el);
  }
}

/** jQuery の :visible と同じく、画面に場所を取っている要素（親が隠れていれば入らない） */
function visibleElements(selector) {
  return Array.from(document.querySelectorAll(selector)).filter(
    (el) => el.getClientRects().length > 0,
  );
}

function showElements(selector) {
  document.querySelectorAll(selector).forEach(showElement);
}

function hideElements(selector) {
  document.querySelectorAll(selector).forEach(hideElement);
}

const fadeAnimationByElement = new WeakMap();

/** selector の文字列・要素・要素の一覧を、要素の配列にそろえる */
function toElementList(target) {
  if (typeof target === 'string') return Array.from(document.querySelectorAll(target));
  if (target instanceof Element) return [target];
  return Array.from(target);
}

/**
 * jQuery の .fadeIn() の置き換え。表示中なら何もしない。フェードアウトの途中なら切り替える。
 * スクロールのたびに呼ばれても、同じ向きのアニメーションを重ねない。
 */
function fadeInElements(target, duration = 400) {
  toElementList(target).forEach((el) => {
    const running = fadeAnimationByElement.get(el);
    if (running?.direction === 'in') return;
    if (!running && getComputedStyle(el).display !== 'none') return;
    running?.animation.cancel();
    showElement(el);
    const animation = el.animate([{ opacity: 0 }, { opacity: 1 }], { duration });
    fadeAnimationByElement.set(el, { direction: 'in', animation });
    animation.onfinish = () => fadeAnimationByElement.delete(el);
  });
}

/** jQuery の .fadeOut() の置き換え。隠れていれば何もしない。終わったら display: none にする */
function fadeOutElements(target, duration = 400) {
  toElementList(target).forEach((el) => {
    const running = fadeAnimationByElement.get(el);
    if (running?.direction === 'out') return;
    if (!running && getComputedStyle(el).display === 'none') return;
    running?.animation.cancel();
    const animation = el.animate([{ opacity: 1 }, { opacity: 0 }], { duration });
    fadeAnimationByElement.set(el, { direction: 'out', animation });
    animation.onfinish = () => {
      hideElement(el);
      fadeAnimationByElement.delete(el);
    };
  });
}

const slideAnimationByElement = new WeakMap();

/**
 * jQuery の .slideToggle() の置き換え。高さと上下の余白を duration ミリ秒で動かして開閉する。
 * 閉じ終わったら display: none にする。onDone は要素ごとに、動き終わったときに呼ぶ
 */
function slideToggleElements(target, duration = 400, onDone) {
  toElementList(target).forEach((el) => {
    const running = slideAnimationByElement.get(el);
    const opening = running ? running.direction === 'up' : getComputedStyle(el).display === 'none';
    running?.animation.cancel();
    showElement(el);

    const style = getComputedStyle(el);
    const properties = ['height', 'paddingTop', 'paddingBottom', 'marginTop', 'marginBottom'];
    const openFrame = Object.fromEntries(properties.map((property) => [property, style[property]]));
    const closedFrame = Object.fromEntries(properties.map((property) => [property, '0px']));

    el.style.overflow = 'hidden';
    // jQuery の既定の動き（swing）に近い緩急
    const animation = el.animate(opening ? [closedFrame, openFrame] : [openFrame, closedFrame], {
      duration,
      easing: 'cubic-bezier(0.37, 0, 0.63, 1)',
    });
    slideAnimationByElement.set(el, { direction: opening ? 'down' : 'up', animation });
    animation.oncancel = () => {
      el.style.overflow = '';
    };
    animation.onfinish = () => {
      el.style.overflow = '';
      if (!opening) hideElement(el);
      slideAnimationByElement.delete(el);
      onDone?.(el);
    };
  });
}
/** DOM の準備と表示の切り替え end */

/** landingUrl begin */
function setLandingUrl() {
  if (getLandingUrl()) return;
  localStorage.setItem('landingUrl', location.href);
}

function getLandingUrl() {
  return localStorage.getItem('landingUrl');
}
onDomReady(setLandingUrl);
/** landingUrl end */

/** fade_in begin */
onDomReady(() => {
  window.addEventListener('scroll', () => {
    const scrollTop = window.scrollY;
    const windowHeight = document.documentElement.clientHeight;
    document.querySelectorAll('.fade_in').forEach((el) => {
      const top = el.getBoundingClientRect().top + window.scrollY;
      if (top - windowHeight + 100 < scrollTop) el.classList.add('fade_animation');
    });
  });
});
/** fade_in end */

/** popup begin */
onDomReady(() => {
  document.querySelectorAll('[data-popup-close]').forEach((el) => {
    el.addEventListener('click', () => {
      el.closest('[data-popup]')?.classList.remove('popup_active');
    });
  });
  document.querySelectorAll('[data-popup]').forEach((el) => {
    el.addEventListener('click', () => {
      el.classList.remove('popup_active');
      document.body.classList.remove('menu-open');
    });
  });
  document.querySelectorAll('[data-popup-view]').forEach((el) => {
    el.addEventListener('click', (event) => {
      event.stopPropagation();
    });
  });
});

function showPopup(key) {
  document.querySelectorAll(`[data-popup="${key}"]`).forEach((el) => {
    el.classList.toggle('popup_active');
  });
}
/** popup end */

/** 最近見た商品 begin */
onDomReady(() => {
  // 最近見た商品に追加
  if (location.pathname.startsWith('/products/')) {
    const handle = location.pathname.split('/')[2];
    addViewedProductHandles(handle);
  }
});

// 最近見た商品を取得
function getViewedProductHandles() {
  try {
    return JSON.parse(localStorage.getItem('viewedProductHandles')) || [];
  } catch {
    return [];
  }
}

// 最近見た商品を保存
function setViewedProductHandles(handles) {
  try {
    localStorage.setItem('viewedProductHandles', JSON.stringify(handles));
  } catch {
    console.error('Failed to set viewed product handles');
  }
}

// 最近見た商品に追加
function addViewedProductHandles(handle) {
  const handles = getViewedProductHandles();
  setViewedProductHandles([handle, ...handles.filter((it) => it !== handle)]);
}
/** 最近見た商品 end */

// 3秒待つ間に鉢・色が変わって再度呼ばれても、表示の予約は1本に保つ
let isCouponPopupScheduled = false;

// 表示可能なら表示する
function tryToShowCouponPopup() {
  if (isCouponPopupScheduled) return;
  if (!shouldShowCouponPopup()) return;

  isCouponPopupScheduled = true;
  setTimeout(() => {
    const dialog = document.getElementById('line-coupon-dialog');
    if (dialog && !dialog.open) dialog.showModal();
    setHadShownCouponPopup();
  }, 3000);
}

function isWithinZitakuPopupDisplayTime() {
  const now = new Date();

  const start = new Date(now.getFullYear(), 4, 11, 12, 0);
  const end = new Date(now.getFullYear(), 4, 13, 23, 59);

  return now >= start && now <= end;
}

async function isPopupDisplayablePage() {
  const excludedPaths = [
    '/cart',
    '/checkout',
    '/pages/personalize',
    '/pages/find_her_favorite_flowers',
    '/pages/custom-order-flower',
    '/account/register',
  ];
  if (excludedPaths.some((path) => location.pathname.startsWith(path))) return false;

  return true;
}

// クーポンモーダルを表示するべきか？
function shouldShowCouponPopup() {
  if (__st.cid) return false; // ログインしていれば表示しない
  if (location.pathname !== '/' && !location.pathname.startsWith('/products/')) return false; // トップと商品ページ以外では表示しない
  if (getHadShownCouponPopup()) return false; // 一度表示していたら表示しない
  return (
    getViewedProductHandles().length >= 3 ||
    getColorOrPotChangedCount() >= 5 ||
    getHadCompletePersonalize() ||
    isUserFromAds()
  );
}

function setHadShownCouponPopup() {
  localStorage.setItem('hadShownCouponPopup', 'true');
}

function getHadShownCouponPopup() {
  return Boolean(localStorage.getItem('hadShownCouponPopup'));
}

function incrementColorOrPotChangedCount() {
  localStorage.setItem('colorOrPotChangedCount', getColorOrPotChangedCount() + 1);
  tryToShowCouponPopup();
}

function getColorOrPotChangedCount() {
  return +localStorage.getItem('colorOrPotChangedCount');
}

function getHadCompletePersonalize() {
  return Boolean(localStorage.getItem('hadCompletePersonalize'));
}

function setHadCompletePersonalize() {
  localStorage.setItem('hadCompletePersonalize', 'true');
}

function isUserFromAds() {
  return getLandingUrl()?.includes('utm_source');
}

/** クーポンpopup end */

/** お気に入り begin */

// お気に入り商品を受注管理画面経由でShopifyに反映
async function applyCustomerFavoriteMetafield(handles) {
  const body = {
    customer_id: currentCustomer?.id,
    customer_email: currentCustomer?.email,
    favorite_product_handles: handles,
  };
  const fetchOptions = {
    method: 'POST',
    headers: new Headers({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
    credentials: 'include',
  };
  return fetch(`https://${adminHost}/update_favorites`, fetchOptions);
}

// ユーザのメタフィールドにあるお気に入りをローカルストレージに反映
function loadCustomerFavoritesToLocalStorage() {
  if (
    !currentCustomer.favoriteProductHandles ||
    currentCustomer.favoriteProductHandles.length === 0
  )
    return;
  if (currentCustomer.favoriteProductHandles === getFavoriteProductHandles()) return;
  setFavoriteProductHandles(currentCustomer.favoriteProductHandles);
}
onDomReady(() => {
  loadCustomerFavoritesToLocalStorage();
});

// お気に入り商品を取得
function getFavoriteProductHandles() {
  try {
    return JSON.parse(localStorage.getItem('favoriteProductHandles')) || [];
  } catch {
    return [];
  }
}

// お気に入り商品が選択されているかを取得
function isFavoriteProductHandle(handle) {
  return getFavoriteProductHandles().includes(handle);
}

// お気に入り商品を保存
function setFavoriteProductHandles(handles) {
  localStorage.setItem('favoriteProductHandles', JSON.stringify(handles));
  applyCustomerFavoriteMetafield(handles);
}

// お気に入り商品に追加
function addFavoriteProductHandle(handle) {
  const handles = getFavoriteProductHandles();
  setFavoriteProductHandles([handle, ...handles.filter((it) => it !== handle)]);
}

// お気に入り商品から削除
function removeFavoriteProductHandle(handle) {
  const handles = getFavoriteProductHandles();
  setFavoriteProductHandles(handles.filter((it) => it !== handle));
}

// お気に入り商品を切り替え
function toggleFavoriteProductHandle(handle) {
  if (isFavoriteProductHandle(handle)) {
    removeFavoriteProductHandle(handle);
  } else {
    addFavoriteProductHandle(handle);
  }
}

/** お気に入り end */

/** 配達日 */
document.addEventListener('DOMContentLoaded', () => {
  // このファイル内の関数（canShipTodaySbs 判定・マルシェ期間判定）が bare 名で呼ぶため window に公開する
  ['getNow', 'getStartOfToday', 'getCurrentHours', 'isSatSunHoliday'].forEach(
    (func) => (window[func] = App[func]),
  );
  extractAndSaveYclidFromUrl();
});

/** 配達日 end */

/** Select */
function putOptions(options, selectSelector, labels = options) {
  const selectEls = document.querySelectorAll(selectSelector);
  const currentVal = selectEls[0]?.value;
  selectEls.forEach((selectEl) => {
    selectEl.replaceChildren(
      ...options.map((option, index) => {
        const optionEl = document.createElement('option');
        optionEl.value = option;
        optionEl.innerHTML = labels[index];
        return optionEl;
      }),
    );
    if (currentVal && options.includes(currentVal)) selectEl.value = currentVal;
  });
}
/** Select end */

/** その他 begin */
function isSmartPhone() {
  return window.matchMedia('(max-width: 640px)').matches;
}
const isProduction = location.host === 'andplants.jp';
const adminHost = isProduction ? '180.andplants.jp' : '004.andplants.jp';

/** その他 end */

/** カート処理 begin */
function showSubmitProceeding() {
  hideElements('[data-submit-button]');
  showElements('[data-submit-proceeding-image]');
}

/** カート処理 end */

/** GAイベント追加 begin */
function sendGaEvent(category, action, label, value = 1) {
  if (typeof ga == 'function') {
    ga('send', {
      hitType: 'event',
      eventCategory: category,
      eventAction: action,
      eventLabel: label,
      eventValue: value,
    });
  }
  /** GA4イベント追加 * */
  if (typeof gtag == 'function') {
    gtag('event', category, {
      TheEventAction: action,
      TheEventLabel: label,
    });
  }
}
/** GAイベント追加 end */

/** パーソナル植物診断 begin */
// 診断を終えたらtrue
function setSessionPersonalized() {
  return sessionStorage.setItem('personalized', true);
}

// 診断が終わっているかどうか
function isSessionPersonalized() {
  return sessionStorage.getItem('personalized') === 'true';
}
/** パーソナル植物診断 end */

/** 汎用関数 begin */
function preloadImage(url) {
  new Image().src = url;
}

function callIfFunc(func) {
  return typeof window[func] === 'function' ? window[func]() : undefined;
}

async function callIfFuncAsync(func) {
  return typeof window[func] === 'function' ? await window[func]() : undefined;
}

function callAfterWindowLoad(func, delay = 0) {
  window.addEventListener('load', () => {
    setTimeout(func, delay);
  });
}

function callAfterDomLoaded(func, delay = 0) {
  document.addEventListener('DOMContentLoaded', () => {
    setTimeout(func, delay);
  });
}

/** 汎用関数 end */

/** 特殊な共通関数 begin */
function intOrNull(value) {
  if (value == null || value === '') return null;
  return +value;
}
function nullOrPositive(int) {
  return int == null || int > 0;
}
/** 特殊な共通関数 end */

/** 即日SBS発送を即日発送の可否に基づいて表示 begin */
onDomReady(() => {
  const canShipTodaySbs = getCurrentHours() < 12 && !isSatSunHoliday(getStartOfToday());
  const canShipTomorrowSbs =
    getCurrentHours() >= 12 && !isSatSunHoliday(App.addDays(getStartOfToday(), 1));
  if (canShipTodaySbs || canShipTomorrowSbs) showElements('[data-sbs-ship-today-label]');
});

function showProductPrice() {
  let isSaleActive;
  if (typeof window.saleManagement !== 'undefined') {
    isSaleActive = window.saleManagement.isWithinSalePeriod();
  } else {
    // フォールバック
    isSaleActive =
      new Date('2026-10-20T00:00') < getNow() && getNow() < new Date('2026-10-20T23:59');
  }

  if (isSaleActive) {
    hideElements('.usually-display');
    showElements('.sale-display');
  } else {
    hideElements('.sale-display');
    showElements('.usually-display');
  }
}

onDomReady(showProductPrice);

function updateMagazineOrJournalPageTitle() {
  const path = window.location.pathname;
  if (path.includes('blogs/magazine') || path.includes('blogs/journal')) {
    const urlParams = new URLSearchParams(window.location.search);
    const pageNum = urlParams.get('page');
    const tagParam = urlParams.get('tag');
    const typeParam = urlParams.get('type');

    if (typeParam) {
      if (tagParam) {
        document.title = `${tagParam}に関する情報まとめ | AND PLANTS (アンドプランツ)`;
        if (magazineMetaDescription[tagParam]) {
          document
            .querySelector('meta[name="description"]')
            .setAttribute('content', magazineMetaDescription[tagParam]);
        }
      }
    }
    if (pageNum && pageNum !== '1') {
      if (path.includes('magazine')) {
        document.title = `${pageNum}ページ目のアンドプランツマガジン | 観葉植物・お花の通販 AND PLANTS (アンドプランツ)`;
      } else if (path.includes('journal')) {
        document.title = `${pageNum}ページ目のアンドプランツジャーナル | 観葉植物・お花の通販 AND PLANTS (アンドプランツ)`;
      }
    }
  }
}

onDomReady(() => {
  updateMagazineOrJournalPageTitle();
});

/** yclid tracking begin */
function setYclid(yclid) {
  if (!yclid) return;
  document.cookie = `yclid=${yclid}; max-age=7776000; path=/`;
}

function getYclid() {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'yclid') {
      return value;
    }
  }
  return null;
}

function extractAndSaveYclidFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const yclid = params.get('yclid');
  if (yclid) {
    setYclid(yclid);
  }
}
/** yclid tracking end */
